/**
 * The tram tracks streamed in: each vector chunk's tram ribbons (style
 * `tram`) as centrelines, kept by chunk as the traffic's lanes are, for the
 * trams to run on (`trams.ts`); and in a network of their own, the
 * railways' (style `rail`), for the trains (`trains.ts`). A tram's way from Västtrafik follows the
 * tracks to within a metre or so on the straight, but cuts across curves by
 * several, and each section laid as a chord on it sat further in still: off
 * the rails. Each section's ends are put on the nearest track instead.
 */

import type { Object3D } from 'three';
import { ROAD_STYLE } from '../core/sources/shortbread.js';

interface ChunkTracks {
  group: Object3D;
  /** Centreline points, x, y, z, chunk-local, and where each run of them ends (exclusive). */
  points: Float32Array;
  ends: Uint32Array;
  /** Segment starts by grid cell, for finding the nearest. */
  cells: Map<number, number[]>;
}

const CELL = 16;
const key = (cx: number, cz: number) => cx * 73856093 ^ cz * 19349663;

export interface TrackPoint {
  x: number;
  y: number;
  z: number;
  /** The track's direction there, unit; either way along it. */
  dx: number;
  dz: number;
}

export class TrackNetwork {
  private readonly chunks = new Map<string, ChunkTracks>();

  /** `style`: which ribbons are its tracks, `ROAD_STYLE.tram` or `ROAD_STYLE.rail`. */
  constructor(private readonly style: number = ROAD_STYLE.tram) {}

  /** A chunk's tracks, from its placed road ribbons (x, y, z positions, two vertices a cross-section). */
  add(id: string, group: Object3D, positions: Float32Array, indices: Uint32Array, attributes: Float32Array): void {
    const pts: number[] = [];
    const ends: number[] = [];
    let previous = -10;
    const quads = Math.floor(indices.length / 6);
    const mid = (v: number) => [(positions[v * 3] + positions[(v + 1) * 3]) / 2, (positions[v * 3 + 1] + positions[(v + 1) * 3 + 1]) / 2, (positions[v * 3 + 2] + positions[(v + 1) * 3 + 2]) / 2];
    for (let q = 0; q < quads; q++) {
      const a = indices[q * 6];
      if (attributes[a * 4 + 2] !== this.style) { previous = -10; continue; }
      if (a !== previous + 2) {
        if (pts.length / 3 > (ends[ends.length - 1] ?? 0)) ends.push(pts.length / 3);
        pts.push(...mid(a));
      }
      pts.push(...mid(a + 2));
      previous = a;
    }
    if (pts.length / 3 > (ends[ends.length - 1] ?? 0)) ends.push(pts.length / 3);
    if (pts.length === 0) { this.chunks.delete(id); return; }
    const points = Float32Array.from(pts);
    const cells = new Map<number, number[]>();
    let start = 0;
    for (const end of ends) {
      for (let i = start; i + 1 < end; i++) {
        const x0 = points[i * 3], z0 = points[i * 3 + 2], x1 = points[(i + 1) * 3], z1 = points[(i + 1) * 3 + 2];
        for (let cx = Math.floor(Math.min(x0, x1) / CELL); cx <= Math.floor(Math.max(x0, x1) / CELL); cx++) {
          for (let cz = Math.floor(Math.min(z0, z1) / CELL); cz <= Math.floor(Math.max(z0, z1) / CELL); cz++) {
            const k = key(cx, cz);
            const list = cells.get(k);
            if (list === undefined) cells.set(k, [i]); else list.push(i);
          }
        }
      }
      start = end;
    }
    this.chunks.set(id, { group, points, ends: Uint32Array.from(ends), cells });
  }

  remove(id: string): void {
    this.chunks.delete(id);
  }

  /**
   * A point on a track, scene metres, between `min` and `max` metres from
   * (x, z), chosen by `rng`: where a train can be set down. Undefined if
   * no track is that far off after a few tries.
   */
  randomPoint(x: number, z: number, min: number, max: number, rng: () => number, into: TrackPoint): boolean {
    const chunks = [...this.chunks.values()].filter(c => c.points.length >= 6);
    for (let attempt = 0; attempt < 24 && chunks.length > 0; attempt++) {
      const chunk = chunks[Math.floor(rng() * chunks.length)];
      const p = chunk.points, n = p.length / 3;
      const i = Math.floor(rng() * (n - 1));
      // Not across the end of one run and the start of the next.
      if ([...chunk.ends].includes(i + 1)) continue;
      const ox = chunk.group.position.x, oy = chunk.group.position.y, oz = chunk.group.position.z;
      const ax = p[i * 3] + ox, az = p[i * 3 + 2] + oz, bx = p[(i + 1) * 3] + ox, bz = p[(i + 1) * 3 + 2] + oz;
      const d = Math.hypot(ax - x, az - z), len = Math.hypot(bx - ax, bz - az);
      if (d < min || d > max || len < 1e-3) continue;
      into.x = ax; into.y = p[i * 3 + 1] + oy; into.z = az;
      into.dx = (bx - ax) / len; into.dz = (bz - az) / len;
      if (rng() < 0.5) { into.dx = -into.dx; into.dz = -into.dz; }
      return true;
    }
    return false;
  }

  /**
   * The track `step` metres on from `from` the way it goes: where a train's
   * front goes next. At a set of points, the way that turns least. False
   * where the track ends, or runs on into ground not streamed in.
   */
  follow(from: TrackPoint, step: number, into: TrackPoint): boolean {
    const x = from.x + from.dx * step, z = from.z + from.dz * step;
    if (!this.snap(x, z, from.dx, from.dz, step * 0.6, into) && !this.snap(x, z, from.dx, from.dz, step * 1.5, into)) return false;
    // Past the end of a track the nearest point is its end, over and over: that is no way on.
    return (into.x - from.x) * from.dx + (into.z - from.z) * from.dz > step * 0.4;
  }

  /**
   * The nearest point on a track to scene (x, z) within `reach`, of a track
   * running the way (dx, dz) goes, either way along it, within about 30°.
   */
  snap(x: number, z: number, dx: number, dz: number, reach: number, into: TrackPoint): boolean {
    let best = reach;
    let found = false;
    for (const chunk of this.chunks.values()) {
      const lx = x - chunk.group.position.x, lz = z - chunk.group.position.z;
      const p = chunk.points;
      const seen = new Set<number>();
      for (let cx = Math.floor((lx - reach) / CELL); cx <= Math.floor((lx + reach) / CELL); cx++) {
        for (let cz = Math.floor((lz - reach) / CELL); cz <= Math.floor((lz + reach) / CELL); cz++) {
          for (const i of chunk.cells.get(key(cx, cz)) ?? []) {
            if (seen.has(i)) continue;
            seen.add(i);
            const ax = p[i * 3], az = p[i * 3 + 2], bx = p[(i + 1) * 3], bz = p[(i + 1) * 3 + 2];
            const sx = bx - ax, sz = bz - az, len = Math.hypot(sx, sz);
            if (len < 1e-4 || Math.abs((sx * dx + sz * dz) / len) < 0.85) continue;
            const t = Math.max(0, Math.min(1, ((lx - ax) * sx + (lz - az) * sz) / (len * len)));
            const qx = ax + sx * t, qz = az + sz * t, d = Math.hypot(qx - lx, qz - lz);
            if (d >= best) continue;
            best = d;
            found = true;
            into.x = qx + chunk.group.position.x;
            into.z = qz + chunk.group.position.z;
            into.y = p[i * 3 + 1] + (p[(i + 1) * 3 + 1] - p[i * 3 + 1]) * t + chunk.group.position.y;
            // Its way along it: the way the tram is going.
            const sign = sx * dx + sz * dz >= 0 ? 1 : -1;
            into.dx = (sx / len) * sign;
            into.dz = (sz / len) * sign;
          }
        }
      }
    }
    return found;
  }
}
